// frontend/src/pages/BodegasPage.jsx
import React, { useState, useEffect } from 'react';
import bodegaService from '../services/bodegaService';
import authService from '../services/authService';
import Loading from '../components/Loading';
import './BodegasPage.css';

const BodegasPage = () => {
  const [bodegas, setBodegas] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBodega, setEditingBodega] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    direccion: '',
    responsable: '',
    status: 'activo'
  });

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    await Promise.all([
      cargarBodegas(),
      cargarStocks(),
      cargarUsuarios()
    ]);
  };

  const cargarBodegas = async () => {
    try {
      setLoading(true);
      const response = await bodegaService.getBodegas();
      // La API devuelve un objeto paginado con la estructura: { count, next, previous, results }
      setBodegas(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar bodegas:', error);
      alert('Error al cargar bodegas');
    } finally {
      setLoading(false);
    }
  };

  const cargarStocks = async () => {
    try {
      const response = await bodegaService.getStocks();
      // La API devuelve un objeto paginado con la estructura: { count, next, previous, results }
      setStocks(response.data.results || response.data);
    } catch (error) {
      console.error('Error al cargar stocks:', error);
    }
  };

  const cargarUsuarios = async () => {
    try {
      // Asumiendo que tienes un endpoint para obtener usuarios
      // Si no existe, puedes adaptarlo según tu estructura
      const user = authService.getUser();
      setUsuarios([user]); // Por ahora solo el usuario actual
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    }
  };

  // Calcular el valor total del inventario (stock valorizado) de una bodega
  const calcularStockValorizado = (bodegaId) => {
    const stocksBodega = stocks.filter(stock => stock.bodega === bodegaId);

    const total = stocksBodega.reduce((sum, stock) => {
      const cantidad = parseFloat(stock.cantidad_actual || 0);
      const precio = parseFloat(stock.precio_costo_unitario || 0);
      return sum + (cantidad * precio);
    }, 0);

    return total;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingBodega) {
        await bodegaService.updateBodega(editingBodega.id, formData);
        alert('Bodega actualizada exitosamente');
      } else {
        await bodegaService.createBodega(formData);
        alert('Bodega creada exitosamente');
      }

      setShowModal(false);
      setEditingBodega(null);
      resetForm();
      cargarDatos();
    } catch (error) {
      console.error('Error al guardar bodega:', error);
      alert('Error al guardar bodega');
    }
  };

  const handleEdit = (bodega) => {
    setEditingBodega(bodega);
    setFormData({
      nombre: bodega.nombre,
      descripcion: bodega.descripcion || '',
      direccion: bodega.direccion || '',
      responsable: bodega.responsable || '',
      status: bodega.status
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta bodega?')) {
      return;
    }

    try {
      await bodegaService.deleteBodega(id);
      alert('Bodega eliminada exitosamente');
      cargarDatos();
    } catch (error) {
      console.error('Error al eliminar bodega:', error);
      alert('Error al eliminar bodega');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      direccion: '',
      responsable: '',
      status: 'activo'
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBodega(null);
    resetForm();
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="bodegas-page">
      <div className="page-header">
        <h1>📦 Gestión de Bodegas</h1>
        <button
          className="btn-primary"
          onClick={() => setShowModal(true)}
        >
          ➕ Nueva Bodega
        </button>
      </div>

      <div className="bodegas-grid">
        {bodegas.length === 0 ? (
          <div className="empty-state">
            <p>No hay bodegas registradas</p>
            <button
              className="btn-primary"
              onClick={() => setShowModal(true)}
            >
              ➕ Crear Primera Bodega
            </button>
          </div>
        ) : (
          bodegas.map((bodega) => (
            <div key={bodega.id} className="bodega-card">
              <div className="card-header">
                <h3>{bodega.nombre}</h3>
                <span className={`status-badge ${bodega.status}`}>
                  {bodega.status === 'activo' ? '✅ Activa' : '❌ Inactiva'}
                </span>
              </div>

              <div className="card-body">
                {bodega.descripcion && (
                  <p className="descripcion">{bodega.descripcion}</p>
                )}

                {bodega.direccion && (
                  <p className="direccion">
                    <strong>📍 Dirección:</strong> {bodega.direccion}
                  </p>
                )}

                {bodega.responsable_nombre && (
                  <p className="responsable">
                    <strong>👤 Responsable:</strong> {bodega.responsable_nombre}
                  </p>
                )}

                {/* Valor Total del Inventario */}
                <div className="stock-valorizado">
                  <p style={{
                    fontSize: '0.8rem',
                    color: '#065f46',
                    fontWeight: '600',
                    marginBottom: '0.25rem'
                  }}>
                    💰 Valor Total del Inventario
                  </p>
                  <p style={{
                    fontSize: '1.375rem',
                    color: '#047857',
                    fontWeight: '700'
                  }}>
                    ${calcularStockValorizado(bodega.id).toLocaleString('es-CL', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })}
                  </p>
                </div>
              </div>

              <div className="card-footer">
                <button
                  className="btn-edit"
                  onClick={() => handleEdit(bodega)}
                >
                  ✏️ Editar
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(bodega.id)}
                >
                  🗑️ Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal para crear/editar bodega */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingBodega ? '✏️ Editar Bodega' : '➕ Nueva Bodega'}</h2>
              <button className="btn-close" onClick={handleCloseModal}>✕</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nombre *</label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  required
                  placeholder="Ej: Bodega Central"
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                  rows={3}
                  placeholder="Descripción de la bodega"
                />
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <textarea
                  value={formData.direccion}
                  onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                  rows={2}
                  placeholder="Dirección física de la bodega"
                />
              </div>

              <div className="form-group">
                <label>Estado</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="activo">Activa</option>
                  <option value="inactivo">Inactiva</option>
                </select>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingBodega ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BodegasPage;
